import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Gusto, PuntoVendita } from '../types';
import moment from 'moment';
import {
  Table as TableIcon,
  Calendar,
  MapPin,
  Search,
  TrendingUp,
  AlertTriangle,
  Layers,
  FileText,
  Package,
  Factory,
  Archive
} from 'lucide-react';
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Alert,
  FormRow,
  FormGroup,
  Label,
  Input,
  Select,
  LoadingState,
  StatCard,
  StatGrid
} from '../components/ui';

// ============================================================
// STYLED COMPONENTS LOCALI (layout specifico della pagina)
// ============================================================

const SectionCard = styled(Card)`
  margin-bottom: var(--space-xl);
`;

const AccessDeniedContainer = styled.div`
  max-width: 500px;
  margin: 4rem auto;
  text-align: center;
  padding: var(--space-2xl);
  background: var(--card-bg);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
`;

// ============================================================
// CONTROLLI E STRUTTURA TABELLE
// ============================================================

const TableControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
  flex-wrap: wrap;
  gap: var(--space-md);
`;

const SearchWrapper = styled.div`
  position: relative;
  min-width: 280px;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SearchInput = styled(Input)`
  width: 100%;
  padding-left: 2.5rem;
  box-sizing: border-box;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  display: flex;
  align-items: center;
`;

const ToggleContainer = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  user-select: none;
  font-weight: var(--font-weight-medium);
`;

const ToggleSwitch = styled.input`
  appearance: none;
  width: 42px;
  height: 22px;
  background-color: var(--gray-300);
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  outline: none;
  transition: background-color var(--transition-fast);

  &::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    background-color: white;
    transition: transform var(--transition-fast);
    box-shadow: var(--shadow-sm);
  }

  &:checked {
    background-color: var(--primary-600);
  }

  &:checked::before {
    transform: translateX(20px);
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--gray-200);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
`;

const TableHead = styled.thead`
  background: var(--gray-50);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--gray-100);
  transition: background-color var(--transition-fast);

  &:last-child {
    border-bottom: none;
  }

  &.totals-row {
    background-color: var(--gray-50);
    border-top: 2px solid var(--gray-300);
    border-bottom: 2px solid var(--gray-300);
    font-weight: bold;
    color: var(--text-primary);
  }
`;

const TableHeaderCell = styled.th<{ sortable?: boolean }>`
  padding: var(--space-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.sortable ? 'var(--gray-100)' : 'transparent'};
  }
`;

const SortIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.35rem;
  vertical-align: middle;
  color: var(--gray-400);
`;

const TableCell = styled.td`
  padding: var(--space-md);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  vertical-align: middle;
`;

const GustoCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const GustoColorDot = styled.div<{ color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${props => props.color};
  box-shadow: var(--shadow-sm);
  border: 1px solid white;
`;

const CategoryBadge = styled.span`
  font-size: var(--font-size-xs);
  background-color: var(--gray-100);
  color: var(--gray-600);
  padding: 0.15rem 0.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: var(--font-weight-medium);
`;

const HighlightValue = styled.span<{ type: 'neutral' | 'success' | 'warning' }>`
  font-weight: var(--font-weight-semibold);
  color: ${props => {
    switch (props.type) {
      case 'success': return 'var(--success)';
      case 'warning': return 'var(--warning)';
      default: return 'var(--text-primary)';
    }
  }};
`;

const WarningLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  color: var(--warning);
  background: var(--warning-soft);
  padding: 0.15rem 0.4rem;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  margin-left: var(--space-sm);
`;

const EmptyState = styled.div`
  padding: var(--space-2xl);
  text-align: center;
  color: var(--gray-400);
  font-size: var(--font-size-base);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-sm);
`;

// ============================================================
// DATA ROW INTERFACE
// ============================================================

interface TableRowData {
  gusto_id: number;
  gusto_nome: string;
  gusto_categoria: string;
  gusto_colore: string;
  giacenza_ieri: number;
  produzione_oggi: number;
  totale_al_banco: number;
  giacenza_oggi: number;
  venduto: number;
}

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

const TabelleAnalisiPage: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState(moment().format('YYYY-MM-DD'));
  const [puntoVenditaId, setPuntoVenditaId] = useState<number | null>(null);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [tableRows, setTableRows] = useState<TableRowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [hideInactiveRows, setHideInactiveRows] = useState(true);

  // Sorting
  const [sortField, setSortField] = useState<keyof TableRowData>('gusto_nome');
  const [sortAsc, setSortAsc] = useState(true);

  // Initial Load
  useEffect(() => {
    loadPuntiVendita();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento una tantum al mount
  }, []);

  // Reload when date or point of sale changes
  useEffect(() => {
    if (data && puntoVenditaId) {
      loadAnalysisData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ricarica solo al cambio di data/punto vendita; loadAnalysisData è ricreata ad ogni render
  }, [data, puntoVenditaId]);

  const loadPuntiVendita = async () => {
    try {
      setLoading(true);
      setError(null);
      const pvList = await ApiService.getPuntiVendita();
      const activePvList = pvList.filter(pv => pv.attivo);
      setPuntiVendita(activePvList);

      if (user?.punto_vendita_id) {
        setPuntoVenditaId(user.punto_vendita_id);
      } else if (activePvList.length > 0) {
        setPuntoVenditaId(activePvList[0].id);
      }
    } catch (err: any) {
      console.error('Errore caricamento punti vendita:', err);
      setError('Impossibile caricare la lista dei punti vendita.');
    } finally {
      setLoading(false);
    }
  };

  const [giacenzaPrecedenteData, setGiacenzaPrecedenteData] = useState<string | null>(null);

  const loadAnalysisData = async () => {
    if (!data || !puntoVenditaId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch dependencies in parallel
      // Usa "ultima-precedente" per trovare l'ultima giacenza registrata
      // PRIMA della data selezionata (non per forza il giorno esatto precedente)
      const [
        gustiMaster,
        giacenzaPrecedenteResponse,
        produzioneOggiResponse,
        giacenzaOggiResponse
      ] = await Promise.all([
        ApiService.getGusti(true),
        ApiService.getUltimaGiacenzaPrecedente({ data, puntoVenditaId }),
        ApiService.getProduzione({ data, puntoVenditaId }),
        ApiService.getGiacenza({ data, puntoVenditaId })
      ]);

      // Salva la data della giacenza precedente per mostrarlo in UI
      const dataGiacPrec = giacenzaPrecedenteResponse?.data
        ? (typeof giacenzaPrecedenteResponse.data === 'string'
            ? giacenzaPrecedenteResponse.data.slice(0, 10)
            : moment(giacenzaPrecedenteResponse.data).format('YYYY-MM-DD'))
        : null;
      setGiacenzaPrecedenteData(dataGiacPrec);

      // Se la giacenza precedente non è del giorno prima esatto,
      // dobbiamo accumulare anche la produzione dei giorni intermedi
      const yesterdayStr = moment(data).subtract(1, 'day').format('YYYY-MM-DD');
      let produzioneIntermediePromise: Promise<any[]> | null = null;

      if (dataGiacPrec && dataGiacPrec < yesterdayStr) {
        // C'è un gap: dobbiamo prendere anche la produzione dal giorno dopo l'ultima giacenza fino a ieri
        const dayAfterGiac = moment(dataGiacPrec).add(1, 'day').format('YYYY-MM-DD');
        // Fetch produzione per ogni giorno nel gap
        produzioneIntermediePromise = (async () => {
          const results: any[] = [];
          let current = new Date(dayAfterGiac);
          const endDate = new Date(yesterdayStr);
          while (current <= endDate) {
            const dateStr = moment(current).format('YYYY-MM-DD');
            try {
              const prod = await ApiService.getProduzione({ data: dateStr, puntoVenditaId });
              if (prod) results.push(...prod);
            } catch {}
            current = new Date(current.getTime() + 86400000);
          }
          return results;
        })();
      }

      // Map production of today
      const prodMap: { [key: number]: number } = {};
      if (produzioneOggiResponse && Array.isArray(produzioneOggiResponse)) {
        produzioneOggiResponse.forEach((prod) => {
          if (prod.gusti && Array.isArray(prod.gusti)) {
            prod.gusti.forEach((g) => {
              prodMap[g.gusto_id] = (prodMap[g.gusto_id] || 0) + (g.quantita_kg || 0);
            });
          }
        });
      }

      // Add intermediate days production if there's a gap
      if (produzioneIntermediePromise) {
        const produzioneIntermedie = await produzioneIntermediePromise;
        produzioneIntermedie.forEach((prod) => {
          if (prod.gusti && Array.isArray(prod.gusti)) {
            prod.gusti.forEach((g: any) => {
              prodMap[g.gusto_id] = (prodMap[g.gusto_id] || 0) + (g.quantita_kg || 0);
            });
          }
        });
      }

      // Map previous stock (last registered before selected date)
      const giacIeriMap: { [key: number]: number } = {};
      if (giacenzaPrecedenteResponse && giacenzaPrecedenteResponse.gusti) {
        giacenzaPrecedenteResponse.gusti.forEach((g) => {
          giacIeriMap[g.gusto_id] = g.quantita_kg || 0;
        });
      }

      // Map today's stock
      const giacOggiMap: { [key: number]: number } = {};
      if (giacenzaOggiResponse && giacenzaOggiResponse.gusti) {
        giacenzaOggiResponse.gusti.forEach((g) => {
          giacOggiMap[g.gusto_id] = g.quantita_kg || 0;
        });
      }

      // Collect all flavor IDs active or present
      const activeFlavorIds = new Set<number>();
      gustiMaster.forEach(g => activeFlavorIds.add(g.id));
      Object.keys(prodMap).forEach(id => activeFlavorIds.add(Number(id)));
      Object.keys(giacIeriMap).forEach(id => activeFlavorIds.add(Number(id)));
      Object.keys(giacOggiMap).forEach(id => activeFlavorIds.add(Number(id)));

      // Create lookup dictionary
      const gustiMap: { [key: number]: Gusto } = {};
      gustiMaster.forEach(g => {
        gustiMap[g.id] = g;
      });

      // Construct rows
      const rows: TableRowData[] = Array.from(activeFlavorIds).map((id) => {
        const detail = gustiMap[id];
        const nome = detail?.nome || `Gusto #${id}`;
        const categoria = detail?.categoria || 'Altro';
        const colore = detail?.colore || 'var(--gray-400)';

        const giacenza_ieri = giacIeriMap[id] || 0;
        const produzione_oggi = prodMap[id] || 0;
        const totale_al_banco = giacenza_ieri + produzione_oggi;
        const giacenza_oggi = giacOggiMap[id] || 0;
        const venduto = totale_al_banco - giacenza_oggi;

        return {
          gusto_id: id,
          gusto_nome: nome,
          gusto_categoria: categoria,
          gusto_colore: colore,
          giacenza_ieri,
          produzione_oggi,
          totale_al_banco,
          giacenza_oggi,
          venduto
        };
      });

      setTableRows(rows);
    } catch (err: any) {
      console.error('Errore nel caricamento dei dati di analisi:', err);
      setError('Impossibile recuperare i dati. Si prega di verificare la connessione o i dati selezionati.');
    } finally {
      setLoading(false);
    }
  };

  // Check Admin role
  if (user?.ruolo !== 'admin') {
    return (
      <AccessDeniedContainer>
        <AlertTriangle size={64} style={{ color: 'var(--error)', marginBottom: '1.5rem' }} />
        <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>
          Accesso Negato
        </h2>
        <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '1.05rem', margin: 0 }}>
          Questa area del portale è riservata esclusivamente agli utenti con privilegi di <strong>Amministratore</strong>.
        </p>
      </AccessDeniedContainer>
    );
  }

  // Handle column header clicks for sorting
  const handleSort = (field: keyof TableRowData) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Sort and filter table rows
  const sortedRows = [...tableRows].sort((a, b) => {
    const valA = a[sortField];
    const valB = b[sortField];

    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
    } else {
      return sortAsc ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
    }
  });

  const filteredRows = sortedRows.filter((row) => {
    const matchesSearch =
      row.gusto_nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.gusto_categoria.toLowerCase().includes(searchQuery.toLowerCase());

    const hasActivity =
      row.giacenza_ieri > 0 ||
      row.produzione_oggi > 0 ||
      row.giacenza_oggi > 0 ||
      row.venduto !== 0;

    return matchesSearch && (!hideInactiveRows || hasActivity);
  });

  // Calculate totals of filtered rows
  const totalGiacenzaIeri = filteredRows.reduce((sum, row) => sum + row.giacenza_ieri, 0);
  const totalProduzioneOggi = filteredRows.reduce((sum, row) => sum + row.produzione_oggi, 0);
  const totalAlBanco = filteredRows.reduce((sum, row) => sum + row.totale_al_banco, 0);
  const totalGiacenzaOggi = filteredRows.reduce((sum, row) => sum + row.giacenza_oggi, 0);
  const totalVenduto = filteredRows.reduce((sum, row) => sum + row.venduto, 0);

  // Group and aggregate by category
  const categoriesMap: {
    [key: string]: {
      nome: string;
      giacenza_ieri: number;
      produzione_oggi: number;
      totale_al_banco: number;
      giacenza_oggi: number;
      venduto: number;
    };
  } = {};

  filteredRows.forEach((row) => {
    const cat = row.gusto_categoria || 'Altro';
    if (!categoriesMap[cat]) {
      categoriesMap[cat] = {
        nome: cat,
        giacenza_ieri: 0,
        produzione_oggi: 0,
        totale_al_banco: 0,
        giacenza_oggi: 0,
        venduto: 0
      };
    }
    categoriesMap[cat].giacenza_ieri += row.giacenza_ieri;
    categoriesMap[cat].produzione_oggi += row.produzione_oggi;
    categoriesMap[cat].totale_al_banco += row.totale_al_banco;
    categoriesMap[cat].giacenza_oggi += row.giacenza_oggi;
    categoriesMap[cat].venduto += row.venduto;
  });

  const categoryRows = Object.values(categoriesMap).sort((a, b) => a.nome.localeCompare(b.nome));

  return (
    <PageContainer>
      <PageHeader
        title="Tabelle Analisi"
        subtitle="Pagina amministratore per l'analisi incrociata di giacenze, produzione e vendite giornaliere."
        icon={<TableIcon />}
        actions={<Badge $variant="accent">Spazio Admin</Badge>}
      />

      <Alert $variant="info">
        <strong>Informazioni per l'amministratore:</strong> Questa pagina temporanea racchiude le tabelle di quadratura della giornata.
        È possibile confrontare i carichi al banco (giacenza sera precedente + produzione odierna) con lo scarico di fine giornata (giacenza sera odierna)
        per calcolare la vendita teorica di ciascun gusto di gelato.
      </Alert>

      {/* PARAMETERS SELECTION */}
      <SectionCard>
        <CardBody>
          <FormRow>
            <FormGroup>
              <Label>
                <Calendar size={14} style={{ verticalAlign: 'text-bottom', marginRight: '0.35rem' }} />
                Giorno di Analisi
              </Label>
              <Input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <MapPin size={14} style={{ verticalAlign: 'text-bottom', marginRight: '0.35rem' }} />
                Punto Vendita
              </Label>
              <Select
                value={puntoVenditaId || ''}
                onChange={(e) => setPuntoVenditaId(parseInt(e.target.value))}
                required
              >
                <option value="">Seleziona punto vendita</option>
                {puntiVendita.map(pv => (
                  <option key={pv.id} value={pv.id}>{pv.nome}</option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>
        </CardBody>
      </SectionCard>

      {/* KPI STATISTICS */}
      {!loading && filteredRows.length > 0 && (
        <StatGrid>
          <StatCard
            icon={<Package />}
            value={`${totalGiacenzaIeri.toFixed(2)} kg`}
            label={`Giacenza Precedente${giacenzaPrecedenteData ? ` (${giacenzaPrecedenteData.split('-').reverse().join('/')})` : ''}`}
          />
          <StatCard
            icon={<Factory />}
            value={`${totalProduzioneOggi.toFixed(2)} kg`}
            label={`Produzione${giacenzaPrecedenteData && giacenzaPrecedenteData < moment(data).subtract(1, 'day').format('YYYY-MM-DD') ? ' (cumulativa)' : ' Oggi'}`}
          />
          <StatCard
            icon={<Layers />}
            value={`${totalAlBanco.toFixed(2)} kg`}
            label="Totale al Banco"
          />
          <StatCard
            icon={<Archive />}
            value={`${totalGiacenzaOggi.toFixed(2)} kg`}
            label="Giacenza Stasera"
          />
          <StatCard
            icon={<TrendingUp />}
            value={`${totalVenduto.toFixed(2)} kg`}
            label="Venduto del Giorno"
          />
        </StatGrid>
      )}

      {/* LOADING & ERROR STATES */}
      {loading && (
        <SectionCard>
          <LoadingState label="Caricamento e analisi dei dati in corso..." />
        </SectionCard>
      )}

      {error && <Alert $variant="error">{error}</Alert>}

      {/* MAIN ANALYSIS TABLE CARD */}
      {!loading && !error && (
        <SectionCard>
          <CardHeader>
            <h2>Quadratura Gusto per Gusto</h2>
          </CardHeader>
          <CardBody>
            <TableControls>
              <SearchWrapper>
                <SearchInput
                  type="text"
                  placeholder="Cerca per gusto o categoria..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <SearchIconWrapper>
                  <Search size={18} />
                </SearchIconWrapper>
              </SearchWrapper>

              <ToggleContainer>
                <ToggleSwitch
                  type="checkbox"
                  checked={hideInactiveRows}
                  onChange={(e) => setHideInactiveRows(e.target.checked)}
                />
                Nascondi gusti senza movimentazione
              </ToggleContainer>
            </TableControls>

            {filteredRows.length === 0 ? (
              <EmptyState>
                <FileText size={48} />
                <p>Nessun dato registrato o nessun gusto movimentato per la data e il punto vendita selezionati.</p>
                <p style={{ fontSize: '0.85rem', margin: 0, opacity: 0.8 }}>
                  Prova a selezionare una data differente o a disattivare il filtro di movimentazione.
                </p>
              </EmptyState>
            ) : (
              <TableWrapper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell sortable onClick={() => handleSort('gusto_nome')}>
                        Gusto {sortField === 'gusto_nome' && <SortIndicator>{sortAsc ? '▲' : '▼'}</SortIndicator>}
                      </TableHeaderCell>
                      <TableHeaderCell sortable onClick={() => handleSort('giacenza_ieri')}>
                        Giacenza Prec.{giacenzaPrecedenteData ? ` (${giacenzaPrecedenteData.split('-').reverse().join('/')})` : ''} (A) {sortField === 'giacenza_ieri' && <SortIndicator>{sortAsc ? '▲' : '▼'}</SortIndicator>}
                      </TableHeaderCell>
                      <TableHeaderCell sortable onClick={() => handleSort('produzione_oggi')}>
                        Produzione{giacenzaPrecedenteData && giacenzaPrecedenteData < moment(data).subtract(1, 'day').format('YYYY-MM-DD') ? ' Cumulativa' : ' Oggi'} (B) {sortField === 'produzione_oggi' && <SortIndicator>{sortAsc ? '▲' : '▼'}</SortIndicator>}
                      </TableHeaderCell>
                      <TableHeaderCell sortable onClick={() => handleSort('totale_al_banco')}>
                        Totale al Banco (A+B) {sortField === 'totale_al_banco' && <SortIndicator>{sortAsc ? '▲' : '▼'}</SortIndicator>}
                      </TableHeaderCell>
                      <TableHeaderCell sortable onClick={() => handleSort('giacenza_oggi')}>
                        Giacenza Oggi (C) {sortField === 'giacenza_oggi' && <SortIndicator>{sortAsc ? '▲' : '▼'}</SortIndicator>}
                      </TableHeaderCell>
                      <TableHeaderCell sortable onClick={() => handleSort('venduto')}>
                        Venduto del Giorno {sortField === 'venduto' && <SortIndicator>{sortAsc ? '▲' : '▼'}</SortIndicator>}
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <TableRow key={row.gusto_id}>
                        <TableCell>
                          <GustoCellContent>
                            <GustoColorDot color={row.gusto_colore} />
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.gusto_nome}</div>
                              <CategoryBadge>{row.gusto_categoria}</CategoryBadge>
                            </div>
                          </GustoCellContent>
                        </TableCell>
                        <TableCell>{row.giacenza_ieri.toFixed(2)} kg</TableCell>
                        <TableCell>
                          {row.produzione_oggi > 0 ? (
                            <HighlightValue type="neutral">{row.produzione_oggi.toFixed(2)} kg</HighlightValue>
                          ) : (
                            <span style={{ color: 'var(--gray-400)' }}>-</span>
                          )}
                        </TableCell>
                        <TableCell style={{ fontWeight: 550 }}>{row.totale_al_banco.toFixed(2)} kg</TableCell>
                        <TableCell>{row.giacenza_oggi.toFixed(2)} kg</TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {row.venduto > 0 ? (
                              <HighlightValue type="success">
                                +{row.venduto.toFixed(2)} kg
                              </HighlightValue>
                            ) : row.venduto < 0 ? (
                              <>
                                <HighlightValue type="warning">
                                  {row.venduto.toFixed(2)} kg
                                </HighlightValue>
                                <WarningLabel title="La giacenza serale supera la giacenza precedente + la produzione. Possibile errore di misurazione.">
                                  <AlertTriangle size={12} /> Rilevazione anomala
                                </WarningLabel>
                              </>
                            ) : (
                              <span style={{ color: 'var(--gray-400)' }}>0.00 kg</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* TOTALS ROW */}
                    <TableRow className="totals-row">
                      <TableCell>TOTALE COMPLESSIVO</TableCell>
                      <TableCell>{totalGiacenzaIeri.toFixed(2)} kg</TableCell>
                      <TableCell>{totalProduzioneOggi.toFixed(2)} kg</TableCell>
                      <TableCell>{totalAlBanco.toFixed(2)} kg</TableCell>
                      <TableCell>{totalGiacenzaOggi.toFixed(2)} kg</TableCell>
                      <TableCell>
                        <span style={{ color: totalVenduto >= 0 ? 'var(--success)' : 'var(--warning)' }}>
                          {totalVenduto.toFixed(2)} kg
                        </span>
                      </TableCell>
                    </TableRow>
                  </tbody>
                </Table>
              </TableWrapper>
            )}
          </CardBody>
        </SectionCard>
      )}

      {/* SECONDARY CATEGORIES ANALYSIS CARD */}
      {!loading && !error && filteredRows.length > 0 && (
        <SectionCard>
          <CardHeader>
            <h2>Raggruppamento per Categoria Prodotti</h2>
          </CardHeader>
          <CardBody>
            <TableWrapper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Categoria</TableHeaderCell>
                    <TableHeaderCell>Giacenza Prec. (A)</TableHeaderCell>
                    <TableHeaderCell>Produzione (B)</TableHeaderCell>
                    <TableHeaderCell>Totale al Banco (A+B)</TableHeaderCell>
                    <TableHeaderCell>Giacenza Oggi (C)</TableHeaderCell>
                    <TableHeaderCell>Venduto Totale</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {categoryRows.map((cat) => (
                    <TableRow key={cat.nome}>
                      <TableCell>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{cat.nome}</div>
                      </TableCell>
                      <TableCell>{cat.giacenza_ieri.toFixed(2)} kg</TableCell>
                      <TableCell>{cat.produzione_oggi.toFixed(2)} kg</TableCell>
                      <TableCell style={{ fontWeight: 550 }}>{cat.totale_al_banco.toFixed(2)} kg</TableCell>
                      <TableCell>{cat.giacenza_oggi.toFixed(2)} kg</TableCell>
                      <TableCell>
                        <HighlightValue type={cat.venduto > 0 ? 'success' : cat.venduto < 0 ? 'warning' : 'neutral'}>
                          {cat.venduto.toFixed(2)} kg
                        </HighlightValue>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </CardBody>
        </SectionCard>
      )}
    </PageContainer>
  );
};

export default TabelleAnalisiPage;
